/* © CNES */
import { Button } from 'primereact/button';
import {
  TreeNodeTemplateOptions,
  TreeTogglerTemplateOptions,
} from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { classNames } from 'primereact/utils';
import React, { MutableRefObject, useContext } from 'react';
import { ConfigContext } from '../../../../context/ConfigContext';
import { CollectionDetails } from '../../../../models/CollectionDetails';

/**
 * Node Template Props
 */
export interface NodeTemplateProps {
  node: TreeNode;
  options: TreeNodeTemplateOptions;
  isUserNotLogged: boolean;
  isDatalakeAvailable: string;
  collectionsList: CollectionDetails[];
  addProduct: (event: React.MouseEvent, label: React.ReactNode) => void;
  productRefs: MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
}

/**
 * Toggler Template Props
 */
export interface TogglerTemplateProps {
  node: TreeNode;
  options: TreeTogglerTemplateOptions;
}

/**
 * Renders a customized tree node template with additional UI components and behaviors.
 *
 *
 * @param node - The current node to be rendered.
 * @param options - Options for rendering the tree node, such as CSS class names.
 * @param isUserNotLogged - A flag indicating if the user is logged out.
 * @param isDatalakeAvailable - The availability status of the datalake (e.g., available, loading, unavailable).
 * @param collectionsList - Detailed information about the collections available for the nodes.
 * @param addProduct - Callback to add the product represented by the node to the user's data collection.
 */
export const NodeTemplate: React.FC<NodeTemplateProps> = ({
  node,
  options,
  isUserNotLogged,
  isDatalakeAvailable,
  collectionsList,
  addProduct,
  productRefs,
}) => {
  const { productsAdded, selectedProduct, setRightPanelVisibility } =
    useContext(ConfigContext);
  const label = <b>{node.label}</b>;
  const isProductAdded = productsAdded.results.includes(node.label!);
  const isDetailsClickable = !node.children;
  const labelClass =
    selectedProduct?.productName === node.label
      ? `${options.className} selected`
      : options.className;

  /**
   * Determines the attributes for the "Add" button based on the user's login status
   * and the availability of the datalake.
   *
   * @returns An object containing the button icon and tooltip text.
   */
  const getAddButtonAttribute = () => {
    let icon,
      tooltipTxt = null;
    if (isUserNotLogged) {
      icon = (
        <img
          alt="lock icon"
          src={require('../../../../assets/icons/lock_icon.png')}
        />
      );
      tooltipTxt = 'Please log in to use this feature';
    } else if (isDatalakeAvailable === 'unavailable') {
      icon = 'pi pi-exclamation-triangle';
      tooltipTxt = 'Datalake unavailable';
    } else {
      if (isProductAdded) {
        icon = 'pi pi-check-circle';
        tooltipTxt =
          isDatalakeAvailable === 'loading'
            ? 'Pending response...'
            : 'Already added to My Data';
      } else {
        icon = 'pi pi-plus-circle';
        tooltipTxt =
          isDatalakeAvailable === 'loading'
            ? 'Pending response...'
            : 'Add to My Data';
      }
    }
    return { icon, tooltipTxt };
  };

  return (
    <div
      ref={(el) => (productRefs.current[label.props.children] = el)}
      style={{ display: 'flex', alignItems: 'center' }}
      onClick={(e) => {
        e.stopPropagation();
        isDetailsClickable &&
          setRightPanelVisibility(
            true,
            collectionsList.find(
              (collection) => collection.productName === label.props.children
            ),
            'Results'
          );
      }}
    >
      <Button
        className={`add-button ${
          isUserNotLogged || isDatalakeAvailable === 'loading' ? 'disabled' : ''
        }`}
        rounded
        text
        icon={getAddButtonAttribute().icon}
        style={
          isProductAdded
            ? { color: 'rgba(0, 176, 80, 1)' }
            : isDatalakeAvailable === 'unavailable'
            ? { color: '#EF4A11' }
            : {}
        }
        aria-label="Add"
        onClick={(e) => {
          e.stopPropagation();
          if (isDatalakeAvailable === 'available') {
            addProduct(e, label);
          }
        }}
        tooltip={getAddButtonAttribute().tooltipTxt}
      />
      <span className={labelClass} title={label.props.children as string}>
        {label}
      </span>
    </div>
  );
};

/**
 * Renders a toggler button for expanding or collapsing tree nodes.
 *
 * @param node - The tree node to which the toggler belongs.
 * @param options - Options for rendering the toggler, such as its expanded state and click handler.
 * @returns A button element that toggles the expansion state of the tree node.
 */
export const TogglerTemplate = ({ node, options }: TogglerTemplateProps) => {
  if (!node) {
    return null;
  }

  const { expanded, onClick } = options;
  const iconClassName = classNames('p-tree-toggler-icon pi pi-fw', {
    'pi-caret-right': !expanded,
    'pi-caret-down': expanded,
  });

  return (
    <button
      data-testid="togglerButton"
      type="button"
      className="p-tree-toggler p-link"
      tabIndex={-1}
      onClick={onClick}
    >
      <span className={iconClassName} aria-hidden="true"></span>
    </button>
  );
};
